/**
 * formatWaterResource — the `sedToText` renderer for the site-specific
 * water-resource section (watershed / aquifer identity from the provider,
 * municipal supply and wastewater service from documents or authored
 * evidence). Kept out of siteEvidence.ts so it is unit-testable and the
 * shared file edit stays a two-line `addSection` call.
 */
import type { WaterResourceEvidence, MunicipalWaterSupply, WastewaterService, WaterEvidenceProvenance } from '../../types/siteEvidence';

function mgd(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${v} MGD` : 'n/a';
}

function miles(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${v} mi` : 'n/a';
}

function provenanceSentence(p: WaterEvidenceProvenance | null | undefined): string {
  if (!p) return '';
  const who = [p.statedBy, p.statedByRole ? `(${p.statedByRole})` : null, p.organisation ? `of ${p.organisation}` : null]
    .filter(Boolean).join(' ');
  const when = p.statedOn ? ` on ${p.statedOn}` : '';
  const scope = p.appliesToProject === 'other-project'
    ? ' — PREPARED FOR ANOTHER PROJECT; treat as indicative, not site-specific'
    : p.appliesToProject === 'this-site' ? ' — prepared for this site' : '';
  const doc = p.documentName ? ` [source: ${p.documentName}]` : p.documentId ? ` [doc ${p.documentId}]` : '';
  const lead = who ? `Stated by ${who}${when}` : when ? `Stated${when}` : 'Attribution not recorded';
  return ` ${lead}${scope}.${doc}`;
}

function supplyLine(s: MunicipalWaterSupply): string {
  const parts = [
    `provider ${s.provider ?? 'n/a'}${s.sourceDescription ? ` (${s.sourceDescription})` : ''}`,
    `average ${mgd(s.avgMgd)}`,
    `peak ${mgd(s.peakMgd)}`,
    `permitted ${mgd(s.permittedMgd)}`,
    `excess ${mgd(s.excessMgd)}`,
    ...(typeof s.storageGallons === 'number' ? [`storage ${s.storageGallons.toLocaleString('en-US')} gal`] : []),
    `nearest main ${miles(s.nearestMainMiles)}`,
    s.lineToSite === true ? 'line reaches the site' : s.lineToSite === false ? 'NO line to the site' : 'line to site unknown',
  ];
  return `Utility water system statistics (average/peak production; permitted/excess system capacity):\nMunicipal water supply — ${parts.join('; ')}.${provenanceSentence(s.provenance)}${s.notes ? ` Notes: ${s.notes}` : ''}`;
}

function wastewaterLine(w: WastewaterService): string {
  const parts = [
    `provider ${w.provider ?? 'n/a'}${w.sourceDescription ? ` (${w.sourceDescription})` : ''}`,
    `permitted ${mgd(w.permittedMgd)}`,
    `average ${mgd(w.avgMgd)}`,
    `peak ${mgd(w.peakMgd)}`,
    `excess ${mgd(w.excessMgd)}`,
    `nearest connection ${miles(w.nearestMainMiles)}`,
    w.lineToSite === true ? 'line reaches the site' : w.lineToSite === false ? 'NO line to the site' : 'line to site unknown',
    w.connectionType ? `connection ${w.connectionType}` : 'connection type unknown',
  ];
  return `Wastewater treatment system statistics (average/peak operating flow; permitted/excess treatment capacity):\nWastewater service — ${parts.join('; ')}.${provenanceSentence(w.provenance)}${w.notes ? ` Notes: ${w.notes}` : ''}`;
}

/** Render the section payload for the whole-SED prompt. */
export function formatWaterResourceForPrompt(d: WaterResourceEvidence): string {
  const lines: string[] = [];
  const watershed = d.huc12?.code ?? d.huc10?.code ?? d.huc8?.code;
  lines.push(
    `Watershed: ${watershed ? `HUC ${watershed}${d.huc12?.name ? ` (${d.huc12.name})` : ''}` : 'n/a'}`
    + ` | Principal aquifer: ${d.principalAquifer?.name ?? 'n/a'} | Sole-source aquifer: ${d.soleSourceAquifer?.name ?? 'none mapped'}`,
  );
  lines.push(
    `Consumptive-use allotment: ${d.consumptiveUseAfy ?? 'n/a'} AFY | Water-rights status: ${d.waterRightsStatus ?? 'n/a'}`
    + ` | Aquifer depth to water: ${d.aquiferDepthToWaterFt ?? 'n/a'} ft | NPDES permit: ${d.npdesPermitId ?? 'n/a'}`,
  );
  if (d.municipalSupply) lines.push(supplyLine(d.municipalSupply));
  if (d.wastewaterService) lines.push(wastewaterLine(d.wastewaterService));
  if (!d.municipalSupply && !d.wastewaterService) {
    lines.push('Municipal water and wastewater utility system statistics are unavailable. Do not infer system production or treatment capacity from project allocation, demand, or discharge. Consult separate source-stated site resource statements for site allocation and project quantities.');
  }
  lines.push(`Basis: ${d.waterResourceBasis} | ${d.disclaimer}`);
  return lines.join('\n');
}
